import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { createPortal } from "react-dom"
import { motion } from "motion/react"
import { useKeyboardInset, useSheetPanelHeight } from "../hooks/useVisualViewportMaxHeight"
import SwipeSheetPanel from "./SwipeSheetPanel"

function useIsMobile() {
    const [mobile, setMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches,
    )

    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)")
        const update = () => setMobile(mq.matches)
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    return mobile
}

/* Жёсткая блокировка скролла страницы: position:fixed на body надёжно отключает
   scroll chaining на iOS (когда внутренний скролл шторки доходит до края и жест
   продолжает листать фон). Счётчик — на случай модалки поверх модалки (промокод) */
let bodyLockCount = 0
let savedScrollY = 0

function lockBodyScroll() {
    if (bodyLockCount === 0) {
        savedScrollY = window.scrollY
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        const { style } = document.body
        style.position = "fixed"
        style.top = `-${savedScrollY}px`
        style.left = "0"
        style.right = "0"
        style.width = "100%"
        if (scrollbarWidth > 0) style.paddingRight = `${scrollbarWidth}px`
    }
    bodyLockCount++
}

function unlockBodyScroll() {
    bodyLockCount = Math.max(0, bodyLockCount - 1)
    if (bodyLockCount === 0) {
        const { style } = document.body
        style.position = ""
        style.top = ""
        style.left = ""
        style.right = ""
        style.width = ""
        style.paddingRight = ""
        window.scrollTo(0, savedScrollY)
    }
}

type ModalProps = {
    children: React.ReactNode
    /** На mobile: панель снизу по высоте контента, не на весь экран */
    sheet?: boolean
    /** Узкая модалка на desktop (auth и т.п.) */
    compact?: boolean
    /** Скрыть drag-ручку (например, когда открыт overlay-шаг) */
    hideHandle?: boolean
    /** Закрытие модалки (по умолчанию navigate(-1)) */
    onDismiss?: () => void
}

const SHEET_WIDE =
    "relative flex h-[92svh] max-h-[92svh] w-full cursor-default flex-col overflow-hidden rounded-t-[28px] md:h-auto md:max-h-[92svh] md:overflow-auto md:max-w-screen-sm md:rounded-[36px] lg:max-w-5xl xl:max-w-7xl"

const SHEET_COMPACT =
    "relative flex h-auto max-h-[min(78dvh,640px)] w-full cursor-default flex-col overflow-hidden rounded-t-[28px] md:h-auto md:max-h-[min(92svh,820px)] md:w-full md:max-w-[428px] md:overflow-hidden md:rounded-[36px]"

export default function Modal({ children, sheet = false, compact = false, hideHandle = false, onDismiss }: ModalProps) {
    const navigate = useNavigate()
    const isMobile = useIsMobile()
    const sheetPanelHeight = useSheetPanelHeight({
        enabled: sheet && isMobile,
        ratio: compact ? 1 : 0.92,
        onlyWhenKeyboard: compact,
    })
    // На iOS fixed-шторка остаётся за клавиатурой — приподнимаем её на высоту клавиатуры.
    // Для compact вместе с height = visualViewport.height панель ровно заполняет
    // видимую область над клавиатурой — без дыр и прыжков
    const keyboardInset = useKeyboardInset(sheet && isMobile)

    useEffect(() => {
        lockBodyScroll()
        return unlockBodyScroll
    }, [])

    // Блокируем прокрутку страницы за модалкой на iOS
    useEffect(() => {
        const preventBodyScroll = (e: TouchEvent) => {
            let target = e.target as HTMLElement | null
            /* Слайдеры обрабатывают touch-жест сами — не глушим их preventDefault'ом */
            if (target?.closest('input[type="range"], [role="slider"]')) return
            while (target && target !== document.body) {
                const { overflowY, overflowX } = window.getComputedStyle(target)
                if ((overflowY === "auto" || overflowY === "scroll") && target.scrollHeight > target.clientHeight + 1) {
                    return
                }
                if ((overflowX === "auto" || overflowX === "scroll") && target.scrollWidth > target.clientWidth + 1) {
                    return
                }
                target = target.parentElement
            }
            e.preventDefault()
        }
        document.addEventListener("touchmove", preventBodyScroll, { passive: false })
        return () => document.removeEventListener("touchmove", preventBodyScroll)
    }, [])

    const dismiss = () => (onDismiss ? onDismiss() : navigate(-1))

    const handleBackdropMouseDown = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            dismiss()
        }
    }

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] flex cursor-pointer items-end justify-center overflow-hidden md:items-center"
            style={{
                background: "rgba(10,18,42,0.75)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
            }}
            onMouseDown={handleBackdropMouseDown}>
            <SwipeSheetPanel
                onDismiss={dismiss}
                hideHandle={hideHandle}
                disableDrag={hideHandle}
                className={
                    sheet
                        ? compact
                            ? SHEET_COMPACT
                            : SHEET_WIDE
                        : "relative flex h-[100dvh] max-h-[100dvh] w-full cursor-default flex-col overflow-hidden md:h-auto md:max-h-[92svh] md:overflow-auto md:max-w-screen-sm md:rounded-[36px] lg:max-w-5xl xl:max-w-7xl"
                }
                style={{
                    background: "linear-gradient(160deg, #1a2d55 0%, #18133b 100%)",
                    borderTop: "1.5px solid rgba(255,255,255,0.12)",
                    // Широкая шторка: явная высота (92% видимой области) вместо svh —
                    // одинаково во всех mobile-браузерах при открытой клавиатуре.
                    // Компактная (auth): только ограничиваем сверху — высота по контенту,
                    // иначе панель растягивается на весь экран над клавиатурой
                    ...(sheetPanelHeight != null
                        ? compact
                            ? { maxHeight: sheetPanelHeight }
                            : { height: sheetPanelHeight, maxHeight: sheetPanelHeight }
                        : {}),
                    ...(keyboardInset > 0
                        ? { marginBottom: keyboardInset, transition: "margin-bottom 0.25s ease" }
                        : {}),
                }}
                motionProps={{
                    initial: { y: "100%", opacity: 0 },
                    animate: { y: 0, opacity: 1 },
                    exit: { y: "100%", opacity: 0 },
                    transition: { type: "spring", stiffness: 260, damping: 30 },
                    onMouseDown: e => e.stopPropagation(),
                }}>
                {children}
            </SwipeSheetPanel>
        </motion.div>,
        document.body,
    )
}
